import * as React from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';

import { cn } from '@/lib/utils';

/* ---- Base Radix avatar (shadcn-standard) ---- */

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      'relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full',
      className
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full object-cover', className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground',
      className
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

/* ---- Enhanced design variant: gradient ring + status dot + plan badge ---- */

type UserAvatarStatus = 'online' | 'offline';

export interface UserAvatarProps
  extends React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root> {
  /** Image source. */
  src?: string;
  /** Alt text for the image. */
  alt?: string;
  /** Fallback content shown while/if the image is unavailable (e.g. initials). */
  fallback?: React.ReactNode;
  /** Wrap the avatar in a brand gradient ring. */
  ring?: boolean;
  /** Show a status dot in the bottom-right corner. Omit to hide. */
  status?: UserAvatarStatus;
  /**
   * Subscription plan code from `user.plan` — drives the emoji badge shown
   * at the bottom-right of the photo. `pro` → 🔥, `premium` → 👑, anything
   * else (including `free`, `null`, `undefined`) → no badge.
   *
   * Case-insensitive. Matches legacy REACTJS `Avatar.jsx` `badge` prop +
   * `common.scss` `.bdg_pro::before { content:"🔥" }` /
   * `.bdg_premium::before { content:"👑" }`.
   */
  plan?: string | null;
}

const PLAN_BADGE: Record<string, string> = {
  pro: '🔥',
  premium: '👑',
};

/**
 * Deterministic fallback-tile palette, ported verbatim from legacy
 * `REACTJS/src/app/components/Avatar.jsx` (the 10-colour `colors` array).
 * Picking by `(charCode(0) + length) % 10` keeps the colour stable for a
 * given name and matches the old site's avatar look pixel-for-pixel.
 */
const AVATAR_PALETTE = [
  '#ec4899',
  '#d946ef',
  '#10b981',
  '#ef4444',
  '#f97316',
  '#84cc16',
  '#f59e0b',
  '#22c55e',
  '#737373',
  '#0ea5e9',
];

/** Resolve a deterministic palette colour from a person name. */
export function avatarColorForName(name?: string | null): string | undefined {
  if (!name) return undefined;
  const trimmed = String(name).trim().toUpperCase();
  if (!trimmed) return undefined;
  const idx = (trimmed.charCodeAt(0) + trimmed.length) % AVATAR_PALETTE.length;
  return AVATAR_PALETTE[idx] ?? '#33B0F2';
}

const statusColor: Record<UserAvatarStatus, string> = {
  online: 'bg-success',
  offline: 'bg-text-muted',
};

/**
 * Design-language avatar: optional brand gradient ring, status dot, and plan badge.
 * All extras are opt-in; with no extra props it renders a plain circular avatar.
 */
const UserAvatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  UserAvatarProps
>(
  (
    { className, src, alt, fallback, ring = false, status, plan, ...props },
    ref
  ) => {
    const planBadge =
      typeof plan === 'string' ? PLAN_BADGE[plan.trim().toLowerCase()] : undefined;
    // Deterministic background colour from the user's name (legacy parity —
    // see `REACTJS/src/app/components/Avatar.jsx`). Used as a paint-behind
    // for the fallback so a string-based fallback (initials) automatically
    // gets the right tile colour; callers passing a custom `<span>` fallback
    // with its own bg will paint over this.
    const fallbackBg = avatarColorForName(alt);
    return (
      <span className="relative inline-block">
        <span
          className={cn(
            'inline-flex rounded-full',
            ring &&
              'bg-gradient-to-br from-brand to-brand-700 p-[2px] shadow-sm'
          )}
        >
          <Avatar
            ref={ref}
            className={cn(ring && 'ring-2 ring-surface', className)}
            {...props}
          >
            {src ? <AvatarImage src={src} alt={alt} /> : null}
            <AvatarFallback
              role="img"
              aria-label={alt || (fallback ? `${fallback} avatar` : 'User avatar')}
              style={fallbackBg ? { backgroundColor: fallbackBg, color: '#fff' } : undefined}
            >
              <span aria-hidden="true">{fallback}</span>
            </AvatarFallback>
          </Avatar>
        </span>

        {status ? (
          <span
            aria-label={status}
            className={cn(
              'absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-surface',
              statusColor[status]
            )}
          />
        ) : null}

        {planBadge ? (
          <span
            aria-label={`${(plan as string).toLowerCase()} plan`}
            title={`${(plan as string).toLowerCase()} plan`}
            // Anchored to the bottom edge of the photo and sized relative to
            // the avatar — matches legacy `Avatar.jsx`'s ph_badge::before.
            // L17: `z-10` keeps the badge above the live-stream red
            // ring that some surfaces (MobileLatestUsers, RightRail)
            // wrap around UserAvatar. Without it the ring's outline
            // could paint over the badge glyph on certain backgrounds.
            // When NO profile photo is present, the avatar shows the
            // coloured initials tile — the badge sits flush with its
            // bottom edge which reads as cropped. Drop it down 5px
            // (`-bottom-[5px]`) so the glyph clears the tile. With a
            // photo, keep the prior flush placement so it overlaps
            // the image edge for the inset look.
            className={cn(
              'pointer-events-none absolute left-1/2 z-10 -translate-x-1/2 text-[1.1em] leading-none drop-shadow',
              src ? 'bottom-0' : '-bottom-[4px]',
            )}
          >
            {planBadge}
          </span>
        ) : null}
      </span>
    );
  }
);
UserAvatar.displayName = 'UserAvatar';

export { Avatar, AvatarImage, AvatarFallback, UserAvatar };
